import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle, CheckCircle2, XCircle, Trash2, Star, MapPin, ShoppingBag, RefreshCw, Pin, PinOff } from "lucide-react";
import axios from "axios";
import StarRow from "../components/StarRow";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminTestimonialsPage = () => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending | approved
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchItems = () => {
    setLoading(true);
    const url = statusFilter === "all"
      ? `${API}/api/admin/testimonials`
      : `${API}/api/admin/testimonials?status=${statusFilter}`;
    axios
      .get(url, { withCredentials: true })
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(fetchItems, [statusFilter]);

  const patch = async (id, payload) => {
    setBusyId(id);
    try {
      const { data } = await axios.put(`${API}/api/admin/testimonials/${id}`, payload, { withCredentials: true });
      setItems((prev) => {
        // If the row no longer matches the active filter (approved status changed), drop it.
        const stillMatches =
          statusFilter === "all" ||
          (statusFilter === "pending" && !data.approved) ||
          (statusFilter === "approved" && data.approved);
        return stillMatches
          ? prev.map((t) => (t.id === id ? { ...t, ...data } : t))
          : prev.filter((t) => t.id !== id);
      });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update review.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/api/admin/testimonials/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      alert("Failed to delete review.");
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = items.filter((t) => !t.approved).length;

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <Link to="/admin/dashboard" className="text-[#1D3557] font-bold flex items-center gap-2 mb-2 hover:text-[#E63946]">
              <ArrowLeft className="w-5 h-5" /> Back to Dashboard
            </Link>
            <h1 className="text-3xl font-black text-[#1D3557] inline-flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              <MessageCircle className="w-8 h-8 text-[#E63946]" />
              Reviews
            </h1>
            <p className="text-[#1D3557] font-medium">
              {items.length} review{items.length === 1 ? "" : "s"}
              {pendingCount > 0 && <span className="text-[#E63946] font-bold"> · {pendingCount} awaiting approval</span>}
            </p>
          </div>
          <button
            onClick={fetchItems}
            className="btn-secondary text-base py-3 px-6 inline-flex items-center gap-2"
            data-testid="refresh-testimonials-btn"
          >
            <RefreshCw className="w-5 h-5" /> Refresh
          </button>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="testimonials-filter">
          {[
            { id: "all", label: "All" },
            { id: "pending", label: `Pending${pendingCount > 0 ? ` (${pendingCount})` : ""}` },
            { id: "approved", label: "Approved" }
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              className={`px-4 py-2 rounded-xl font-bold text-sm border-3 border-[#1D3557] transition-all ${
                statusFilter === f.id
                  ? "bg-[#1D3557] text-white shadow-[3px_3px_0px_0px_rgba(29,53,87,1)]"
                  : "bg-white text-[#1D3557] hover:shadow-[3px_3px_0px_0px_rgba(29,53,87,1)]"
              }`}
              style={{ fontFamily: "'Fredoka', sans-serif" }}
              data-testid={`testimonials-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-16 h-16 border-4 border-[#E63946] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="tactile-card p-10 text-center">
            <MessageCircle className="w-16 h-16 text-[#1D3557]/30 mx-auto mb-3" />
            <p className="text-[#1D3557]/70 font-bold">No reviews here yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((t) => (
              <motion.article
                key={t.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="tactile-card p-5 bg-white"
                data-testid={`admin-testimonial-${t.id}`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <StarRow value={t.rating} size={20} />
                  {t.approved ? (
                    <span className="text-xs font-black px-2 py-1 rounded-full bg-[#2A9D8F] text-white border-2 border-[#1D3557]">
                      Approved
                    </span>
                  ) : (
                    <span className="text-xs font-black px-2 py-1 rounded-full bg-[#E9C46A] text-[#1D3557] border-2 border-[#1D3557]" data-testid={`pending-badge-${t.id}`}>
                      Pending
                    </span>
                  )}
                  {t.source === "amazon" && (
                    <span className="text-xs font-black px-2 py-1 rounded-full bg-[#FF9900] text-[#1D3557] border-2 border-[#1D3557] inline-flex items-center gap-1">
                      <ShoppingBag className="w-3 h-3" />
                      Amazon
                    </span>
                  )}
                  {t.featured && (
                    <span className="text-xs font-black px-2 py-1 rounded-full bg-[#E63946] text-white border-2 border-[#1D3557] inline-flex items-center gap-1">
                      <Star className="w-3 h-3" fill="currentColor" />
                      Featured
                    </span>
                  )}
                </div>
                {t.title && (
                  <h3 className="font-black text-[#1D3557] text-lg mb-1" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {t.title}
                  </h3>
                )}
                <p className="text-[#1D3557] font-medium whitespace-pre-line">{t.body}</p>
                <div className="mt-3 pt-3 border-t border-[#1D3557]/10 flex flex-wrap items-center gap-3 text-sm">
                  <span className="font-bold text-[#1D3557]">— {t.reviewer_name}</span>
                  {t.location && (
                    <span className="text-[#1D3557]/70 font-medium inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {t.location}
                    </span>
                  )}
                  {t.email && (
                    <span className="text-[#1D3557]/60 font-medium">{t.email}</span>
                  )}
                  <span className="text-[#1D3557]/50 font-medium">
                    {new Date(t.created_at).toLocaleDateString()}
                  </span>
                </div>

                {/* Actions */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {!t.approved && (
                    <button
                      type="button"
                      onClick={() => patch(t.id, { approved: true })}
                      disabled={busyId === t.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#2A9D8F] hover:bg-[#1D3557] text-white text-xs font-bold transition-colors disabled:opacity-60"
                      data-testid={`approve-btn-${t.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4" /> Approve
                    </button>
                  )}
                  {t.approved && (
                    <button
                      type="button"
                      onClick={() => patch(t.id, { approved: false })}
                      disabled={busyId === t.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E9C46A] hover:bg-[#1D3557] hover:text-white text-[#1D3557] text-xs font-bold transition-colors disabled:opacity-60"
                      data-testid={`unapprove-btn-${t.id}`}
                    >
                      <XCircle className="w-4 h-4" /> Unapprove
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => patch(t.id, { featured: !t.featured })}
                    disabled={busyId === t.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border-2 border-[#1D3557] text-[#1D3557] hover:bg-[#1D3557] hover:text-white text-xs font-bold transition-colors disabled:opacity-60"
                    data-testid={`feature-btn-${t.id}`}
                  >
                    {t.featured ? <><PinOff className="w-4 h-4" /> Unfeature</> : <><Pin className="w-4 h-4" /> Feature</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(t.id)}
                    disabled={busyId === t.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border-2 border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white text-xs font-bold transition-colors disabled:opacity-60"
                    data-testid={`delete-btn-${t.id}`}
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTestimonialsPage;
